import React, { useState, useEffect, useMemo } from 'react';
import { Download, X, FileSpreadsheet, Check } from 'lucide-react';
import Modal from '../../../components/UI/Modal';
import ProgressModal from '../../../components/UI/ProgressModal';
import {
  exportOvertimeMonitoringWorkbook,
  buildScopeLabel,
} from '../../../utils/overtimeMonitoringExport';

/**
 * OvertimeExportModal — month picker + Excel generator for the
 * 72-Hour Overtime Monitoring "employees" tab.
 *
 * HR mental model this serves:
 *   "I want this month, or a run of months (e.g. Jan–Jun), in ONE polished
 *    Excel I can forward to MOM / management — not six separate downloads."
 *
 * Flow:
 *   1. Tick the months to include (the month currently on screen is
 *      pre-ticked). The active table filters (department / trade / status
 *      / discrepancy / search) are shown and carried into every month so
 *      the export matches exactly what HR was looking at.
 *   2. On "Export", we pull each month's roster in sequence — reusing the
 *      already-loaded current month so there's zero wait for the common
 *      single-month case — then hand the set to the workbook builder.
 *
 * Per the design system, a batch operation with KNOWN progress (month i of
 * n) uses the shared ``ProgressModal`` (phase stepper) — not ``Processing
 * Modal`` (indeterminate) and not a hand-rolled progress bar. The picker
 * and the progress view are mutually exclusive (only one modal is mounted
 * at a time), so there is never a modal-on-modal stack.
 */
const OvertimeExportModal = ({
  isOpen,
  onClose,
  monthOptions = [],
  defaultMonth = '',
  scopeFilters = {},
  preloaded = null,
  fetchMonth,
}) => {
  const [selected, setSelected] = useState(() => new Set());
  const [phase, setPhase] = useState('select'); // 'select' | 'working' | 'error'
  const [chosen, setChosen] = useState([]);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);

  const scopeLabel = useMemo(() => buildScopeLabel(scopeFilters), [scopeFilters]);

  // Reset to a clean "select" state every time the modal is opened,
  // pre-ticking the month the user is currently viewing.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(defaultMonth ? [defaultMonth] : []));
      setPhase('select');
      setChosen([]);
      setPhaseIdx(0);
      setErrorMsg(null);
    }
  }, [isOpen, defaultMonth]);

  if (!isOpen) return null;

  const working = phase === 'working';

  const toggleMonth = (value) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(monthOptions.map((m) => m.value)));
  const clearAll = () => setSelected(new Set());

  const selectedCount = selected.size;

  const handleExport = async () => {
    if (selectedCount === 0) return;

    // Newest first so progress + sheet order match the dashboard.
    const months = monthOptions
      .filter((m) => selected.has(m.value))
      .sort((a, b) => (a.value < b.value ? 1 : -1));

    setChosen(months);
    setPhaseIdx(0);
    setErrorMsg(null);
    setPhase('working');

    const filterParams = {
      department: scopeFilters.department || '',
      trade: scopeFilters.trade || '',
      riskLevel: scopeFilters.riskLevel || '',
      hasDiscrepancy: scopeFilters.hasDiscrepancy || '',
      search: scopeFilters.search || '',
    };

    try {
      const built = [];
      for (let i = 0; i < months.length; i += 1) {
        const opt = months[i];
        setPhaseIdx(i);

        // Reuse the roster already loaded on screen for the active month
        // (instant — no second 40-60s snapshot build for the common case).
        if (preloaded && preloaded.yearMonth === opt.value && Array.isArray(preloaded.employees)) {
          built.push({
            yearMonth: opt.value,
            label: opt.label,
            isCurrentMonth: !!preloaded.isCurrentMonth,
            employees: preloaded.employees,
          });
          continue;
        }

        // eslint-disable-next-line no-await-in-loop
        const { employees, isCurrentMonth } = await fetchMonth(opt.value, filterParams);
        built.push({
          yearMonth: opt.value,
          label: opt.label,
          isCurrentMonth,
          employees,
        });
      }

      // Mark every phase complete while the workbook serialises + downloads.
      setPhaseIdx(months.length);
      await exportOvertimeMonitoringWorkbook({ months: built, scopeLabel });
      onClose?.();
    } catch (err) {
      console.error('Overtime export failed:', err);
      setErrorMsg(
        err?.response?.data?.error
        || err?.message
        || 'Failed to build the export. Please try again.',
      );
      setPhase('error');
    }
  };

  // ── Progress / error view (DS-mandated ProgressModal) ──────────────────
  if (working || phase === 'error') {
    const currentLabel = chosen[Math.min(phaseIdx, chosen.length - 1)]?.label || '';
    return (
      <ProgressModal
        isOpen
        title="Exporting Overtime Monitoring"
        phases={chosen.map((m) => ({ label: m.label }))}
        currentPhase={phaseIdx}
        progress={null}
        statusMessage={
          working
            ? `Building monthly snapshot — ${currentLabel} (${Math.min(phaseIdx + 1, chosen.length)} of ${chosen.length})`
            : ''
        }
        error={errorMsg}
        onDismissError={() => {
          setErrorMsg(null);
          setPhase('select');
        }}
      />
    );
  }

  // ── Month picker view ──────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      theme="admin"
      contentClassName="space-y-5"
      customHeader={(
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Export to Excel</h3>
              <p className="text-sm text-gray-500">
                72-Hour Overtime Monitoring · combine multiple months into one file
              </p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close-admin" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-admin-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={selectedCount === 0}
            className="btn btn-admin-primary"
          >
            <Download className="w-4 h-4" />
            {selectedCount === 0
              ? 'Export'
              : `Export ${selectedCount} ${selectedCount === 1 ? 'month' : 'months'}`}
          </button>
        </>
      )}
    >
            {/* Active scope — what HR is currently filtered to */}
            <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Applied to every selected month
              </p>
              <p className="text-sm text-gray-800">{scopeLabel}</p>
            </div>

            {/* Month picker */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">
                  Select months
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {selectedCount} selected
                  </span>
                </p>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-red-600 hover:text-red-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                  >
                    Select all
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-gray-500 hover:text-gray-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 rounded"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                {monthOptions.map((m) => {
                  const isChecked = selected.has(m.value);
                  const isCurrent = m.value === defaultMonth;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => toggleMonth(m.value)}
                      aria-pressed={isChecked}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${
                        isChecked
                          ? 'border-red-500 bg-red-50 text-gray-900'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                          isChecked ? 'bg-red-600 border-red-600' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="truncate">
                        {m.label}
                        {isCurrent && (
                          <span className="ml-1 text-[10px] text-red-600 font-medium">• current</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className="mt-4 text-xs text-gray-500">
                All selected months are combined into a single workbook — one sheet per
                month, with a comparison summary when you pick more than one.
              </p>
            </div>
    </Modal>
  );
};

export default OvertimeExportModal;
